/* eslint-disable max-lines */
import { Box, Icon, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef, GridValueFormatterParams, GridValueGetterParams } from '@mui/x-data-grid-pro';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link, LinkProps } from 'react-router-dom';
import { throwError } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError } from 'rxjs/operators';

import { PaginatedDataGrid } from 'app/components/paginated-data-grid';
import { PatternTypeSelector } from 'app/components/pattern-type-selector';
import { StorePicker } from 'app/components/store-picker';
import { CrisRequestType } from 'app/constants/cris-request-type';
import { HttpCodes } from 'app/constants/http-codes';
import { makeBlankStore, Store } from 'app/model/cris/store';
import { ListOptions } from 'app/model/list-options';
import { searchService } from 'app/services/search-service';
import { storeService } from 'app/services/store.service';
import { PreProdPattern } from 'app/type';

interface SearchProps {
    store?: Store;
    type: CrisRequestType;
    patternId: string;
}

export const PatternExplorer = (): ReactElement => {
    const location = useLocation();
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);
    const storeId = Number(queryParams.get('store'));
    const type = queryParams.get('type');
    const patId = queryParams.get('patternId');
    const [patternType, setPatternType] = useState(type ? (type as CrisRequestType) : CrisRequestType.STORE);
    const [patternId, setPatternId] = useState(patId ?? '');
    const [store, setStore] = useState(
        storeId ? makeBlankStore({ id: storeId, name: `Store Id ${storeId}` }) : undefined,
    );

    const onSearch = useCallback((opts: ListOptions, searchProps: SearchProps) => {
        return searchService.findPatterns(searchProps.type, searchProps.store, opts, searchProps.patternId);
    }, []);

    useEffect(() => {
        if (store) {
            const id = store.id;
            storeService
                .get(id)
                .pipe(
                    catchError((error: AjaxError) => {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                        if (error.status === HttpCodes.NOT_FOUND) {
                            setStore(makeBlankStore({ id, name: `Invalid Store Id: ${id}` }));
                        }

                        return throwError(() => error);
                    }),
                )
                .subscribe((newStore) => {
                    setStore(newStore);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        history.push({
            search: `?${new URLSearchParams({
                store: store ? String(store.id) : '',
                type: patternType,
                patternId,
            }).toString()}`,
        });
    }, [patternType, store, history, patternId]);

    const resultColumns = useMemo(() => {
        const columns: Array<GridColDef<PreProdPattern>> = [
            {
                field: 'actions',
                headerName: 'Actions',
                width: 75,
                type: 'actions',
                getActions: (params) => {
                    const urlSearchParams = new URLSearchParams([
                        ...Object.entries({ ...params.row, ...(store && { store: store.id }) }).reduce<
                            Array<[string, string]>
                        >((arr, [key, value]) => {
                            if (Array.isArray(value)) {
                                value.forEach((val) => {
                                    if (val) {
                                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                        arr.push([key, `${val}`]);
                                    }
                                });
                            } else if (value && 'object' !== typeof value) {
                                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                arr.push([key, `${value}`]);
                            }

                            return arr;
                        }, []),
                    ]);

                    return [
                        <GridActionsCellItem
                            {...{
                                to: {
                                    pathname: '/explore/tuples',
                                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                    search: `?${urlSearchParams}`,
                                } satisfies LinkProps['to'],
                            }}
                            component={Link}
                            icon={<Icon>manage_search</Icon>}
                            label='Search'
                        />,
                    ];
                },
            },
            { field: 'id', headerName: 'Pattern Id', width: 100 },
            { field: 'mtid', headerName: 'MTID', width: 100 },
            {
                field: 'store',
                headerName: 'Store',
                width: 150,
                valueGetter: (params: GridValueGetterParams<PreProdPattern>) => {
                    if (params.row.type === CrisRequestType.STORE) {
                        const storeData = params.row.store;
                        const storeName = storeData.name;
                        const id = storeData.id;
                        const storeBrand = storeData.brand?.name ?? 'Unknown Brand';

                        return `[${id}] ${storeName} (${storeBrand})`;
                    }

                    const brandData = params.row.brand;
                    const brandName = brandData.name;
                    const Id = brandData.id;

                    return `[${Id}] ${brandName}`;
                },
            },
        ];

        if (patternType === CrisRequestType.STORE) {
            columns.push({ field: 'action', headerName: 'Action', width: 100 });
        }

        columns.push(
            ...[
                { field: 'matchMethod', headerName: 'Match method', width: 120 },
                { field: 'mid', headerName: 'Mid', width: 100 },
                { field: 'merchdesc', headerName: 'Descriptor', width: 250 },
                { field: 'mccOperator', headerName: 'MCC Operator', width: 100 },
                {
                    field: 'mcc',
                    headerName: 'MCC',
                    width: 100,
                    valueGetter: (params: GridValueGetterParams<PreProdPattern, PreProdPattern['mcc']>) =>
                        params.value?.join(', '),
                },
                { field: 'locationOperator', headerName: 'LocationOperator', width: 100 },
                { field: 'city', headerName: 'City', width: 100 },
                { field: 'state', headerName: 'State', width: 100 },
                { field: 'zip', headerName: 'Zip', width: 100 },
            ],
        );

        if (patternType === CrisRequestType.STORE) {
            columns.push({ field: 'country', headerName: 'Country', width: 100 });
        }
        columns.push({
            field: 'active',
            headerName: 'Active',
            width: 100,
            valueFormatter: (params: GridValueFormatterParams) => (params.value ? 'Y' : 'N'),
        });

        if (patternType === CrisRequestType.CHANNEL) {
            columns.push({ field: 'subChannel', headerName: 'SubChannel', width: 100 });
        }

        if (patternType === CrisRequestType.LOCATION) {
            columns.push({ field: 'extractField', headerName: 'Store # Extract Field', width: 100 });
            columns.push({ field: 'extractPattern', headerName: 'Extract Pattern', width: 100 });
        }

        return columns;
    }, [patternType, store]);

    const download = React.useCallback(() => {
        if (!store && !patternId) {
            return;
        }

        searchService.downloadPatterns(patternType, store, patternId);
    }, [store, patternType, patternId]);

    const MemoizedPaginatedDataGrid = useMemo(
        () => (
            <PaginatedDataGrid
                dataSide='server'
                autoHeight
                density='compact'
                columns={resultColumns}
                searchProps={{ store, type: patternType, patternId }}
                onSearch={onSearch}
            />
        ),
        [resultColumns, store, patternType, patternId, onSearch],
    );

    return (
        <>
            <Typography variant='h4'>Pattern Explorer</Typography>
            <Typography variant='body1' className='mb-2'>
                Find deployed patterns for any store. Start typing store name to show matching results.
            </Typography>
            <Box display='flex' className='my-2' alignItems='center'>
                <Box>
                    <PatternTypeSelector
                        value={patternType}
                        onChange={(evt) => {
                            setPatternType(evt.target.value as CrisRequestType);
                        }}
                    />
                </Box>
                <Box flexGrow={1} className='px-2'>
                    <StorePicker
                        size='small'
                        value={store}
                        onChange={(event, newStore) => {
                            setStore(newStore);
                            setPatternId('');
                        }}
                    />
                </Box>
                <Box flexGrow={1} mx={1}>
                    <TextField
                        name='pattern'
                        label='Pattern Id'
                        variant='outlined'
                        size='small'
                        value={patternId}
                        onChange={(evt) => {
                            setPatternId(evt.target.value);
                        }}
                        fullWidth
                        helperText='Separate multiple patterns with commas'
                        FormHelperTextProps={{
                            sx: { margin: 0, position: 'absolute', bottom: '-20px' },
                        }}
                    />
                </Box>
                <Tooltip title='Download Patterns'>
                    <IconButton color='primary' onClick={download} size='large'>
                        <Icon>file_download</Icon>
                    </IconButton>
                </Tooltip>
            </Box>
            {(store || patternId) && MemoizedPaginatedDataGrid}
        </>
    );
};
